'use client';

import { useCallback } from 'react';
import { useOptionalWorkItemOpener } from '@/contexts/WorkItemOpenerContext';
import type { WorkItem } from '@/components/UniversalWorkItems';
import { authService } from '@/lib/authService';
import { emit } from '@/lib/typedEventBus';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

function buildOptimisticTeamMemberWorkItem(userId: number): WorkItem {
  const timestamp = new Date().toISOString();

  return {
    id: `team-member-${userId}`,
    item_number: `USR-${userId}`,
    item_type: 'team_member',
    item_subtype: 'user',
    title: '',
    description: 'Loading live profile…',
    status: 'active',
    assigned_to: userId,
    created_by: userId,
    custom_fields: {
      user_id: userId,
    },
    created_at: timestamp,
    updated_at: timestamp,
  } as WorkItem;
}

async function fetchTeamMemberProfile(userId: number): Promise<WorkItem | null> {
  const token = authService.getTokens()?.access_token;
  if (!token) {
    return null;
  }

  try {
    const response = await fetch(`/api/v1/team/members/${userId}/profile`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      return null;
    }

    const payload = await response.json().catch(() => null);
    return payload?.success && payload.data ? payload.data as WorkItem : null;
  } catch (error) {
    if (!isSilentFetchError(error)) console.error('[useOpenTeamMember] Failed to prefetch team member profile:', error);
    return null;
  }
}

/**
 * Returns a callback that fetches a team member profile and opens it
 * in the WorkItemDetailModal via WorkItemOpenerContext.
 *
 * Usage:
 *   const openTeamMember = useOpenTeamMember();
 *   <WorkItemsDataGrid onOpenTeamMember={openTeamMember} />
 */
export function useOpenTeamMember(): (userId: number) => void {
  const workItemOpener = useOptionalWorkItemOpener();

  return useCallback(async (userId: number) => {
    try {
      if (!Number.isFinite(userId) || userId <= 0) {
        return;
      }

      const teamMemberItem = await fetchTeamMemberProfile(userId)
        || buildOptimisticTeamMemberWorkItem(userId);

      if (workItemOpener?.openWorkItemDirect) {
        workItemOpener.openWorkItemDirect(teamMemberItem);
        return;
      }

      emit('openWorkItem', { workItem: teamMemberItem });

      console.warn('[useOpenTeamMember] WorkItemOpenerProvider is not mounted; team member profile could not be opened in-place');
    } catch (err) {
      console.error('[useOpenTeamMember] Error opening team member profile:', err);
    }
  }, [workItemOpener]);
}
